import { lazy, Suspense, useState } from 'react';
import { Menu, Search, Info, Sun, Moon, Monitor } from 'lucide-react';
import { Button, Badge, Tooltip } from '@/shared/ui';
import { useTheme } from '@/app/providers/ThemeProvider';
import { BrandMark } from '@/shared/ui/BrandMark';

const ClientSafeModal = lazy(() =>
  import('./ClientSafeModal').then((module) => ({ default: module.ClientSafeModal })),
);

export function Navbar({
  onMenuClick,
  onSearchClick,
}: {
  onMenuClick?: () => void;
  onSearchClick?: () => void;
}) {
  const [showSafeModal, setShowSafeModal] = useState(false);
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme(document.documentElement.classList.contains('dark') ? 'light' : 'dark');
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-5 w-5" />;
    if (theme === 'dark') return <Moon className="h-5 w-5" />;
    return <Monitor className="h-5 w-5" />;
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b bg-background px-3 sm:gap-4 sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent"
        onClick={onMenuClick}
        leftIcon={<Menu className="h-5 w-5" />}
        aria-label="Toggle menu"
      />

      <div className="flex shrink-0 items-center gap-2 md:hidden">
        <BrandMark className="h-5 w-5 text-primary" />
        <span className="hidden text-lg font-bold min-[360px]:inline">EasyUtils</span>
      </div>

      <div className="ml-auto min-w-0 flex-none sm:ml-0 sm:flex-1">
        <Button
          variant="outline"
          className="h-10 w-10 justify-center border-transparent bg-muted px-0 text-foreground hover:bg-muted/80 sm:w-64 sm:justify-start sm:px-4"
          onClick={onSearchClick}
        >
          <span className="sr-only">Search tools</span>
          <Search className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Search tools...</span>
          <kbd className="pointer-events-none ml-auto hidden h-5 select-none items-center gap-1 rounded border bg-background px-1.5 font-mono text-[10px] font-medium text-foreground sm:inline-flex">
            <span className="text-xs">⌘/Ctrl</span>K
          </kbd>
        </Button>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Tooltip
          content="Review what stays in the browser and what a tool sends over the network"
          position="bottom"
        >
          <Badge
            variant="outline"
            role="button"
            tabIndex={0}
            aria-label="Data handling - Click to review privacy details"
            className="group hidden sm:inline-flex items-center gap-1.5 cursor-pointer border-border bg-muted text-foreground hover:bg-accent hover:text-foreground transition-all shadow-xs hover:shadow-sm px-2.5 py-1 text-xs font-semibold rounded-full"
            onClick={() => setShowSafeModal(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowSafeModal(true);
              }
            }}
          >
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>Data handling</span>
            <Info className="h-3 w-3 opacity-60 group-hover:opacity-100 transition-opacity shrink-0 ml-0.5" />
          </Badge>
        </Tooltip>

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full text-muted-foreground hover:text-foreground hover:bg-accent"
          leftIcon={getThemeIcon()}
          aria-label="Toggle theme"
        />
      </div>

      {showSafeModal && (
        <Suspense fallback={null}>
          <ClientSafeModal open onOpenChange={setShowSafeModal} />
        </Suspense>
      )}
    </header>
  );
}
